import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/common/Reveal";
import Counter from "@/components/common/Counter";
import { SERVICES, CLIENTS, VALUES, COMPANY } from "@/data/content";

const HERO_IMAGES = [
  "/projects/shore-01.jpg",
  "/projects/shagaya-main-01.jpg",
  "/projects/ncs-accommodation.jpg",
];

export default function Home() {
  const [heroIdx, setHeroIdx] = useState(0);
  const [stats, setStats] = useState({ projects_delivered: 50, years_experience: 12, clients_served: 35, team_size: 48 });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([api.get("/stats"), api.get("/projects", { params: { featured: true, limit: 4 } })]);
        setStats(s.data);
        setFeatured(p.data);
      } catch {}
    })();
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative h-[72vh] min-h-[480px] md:h-[78vh] w-full overflow-hidden" data-testid="home-hero">
        {HERO_IMAGES.map((src, i) => (
          <motion.div
            key={src}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${src})` }}
            initial={false}
            animate={{ opacity: i === heroIdx ? 1 : 0, scale: i === heroIdx ? 1.05 : 1 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: 6, ease: "linear" } }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/80" />
        <div className="absolute inset-0 grain" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 flex flex-col justify-end pb-14 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3 }}
            className="max-w-3xl"
          >
            <div className="mono-label text-white/70 mb-4 md:mb-6" data-testid="hero-tagline">
              <span className="inline-block w-6 md:w-8 h-px bg-[#BE1E2D] align-middle mr-2 md:mr-3" />
              Mohr Engineering · Abuja, Nigeria
            </div>
            <h1 className="font-display text-white text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1] tracking-[-0.02em] font-light">
              We Build <em className="not-italic text-[#BE1E2D] font-normal">Experiences.</em>
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-xl mt-4 md:mt-5 leading-relaxed">
              Turnkey civil engineering & general contracting, delivered with the precision of a chartered practice and the discipline of a builder who owns the site.
            </p>
            <div className="flex flex-wrap gap-3 mt-6 md:mt-8">
              <Link to="/portfolio" className="btn-primary" data-testid="hero-cta-portfolio">
                View Our Work <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline-light" data-testid="hero-cta-contact">
                Start a Project <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Slide indicator */}
        <div className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-4">
          <span className="mono-label text-white/80 tabular-nums">
            {String(heroIdx + 1).padStart(2, "0")} / {String(HERO_IMAGES.length).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-2">
            {HERO_IMAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-px transition-all duration-500 ${i === heroIdx ? "bg-white w-16" : "bg-white/40 w-8"}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAR — overlapping */}
      <section className="relative -mt-10 md:-mt-16 z-20 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14" data-testid="stats-bar">
        <div className="bg-white border border-zinc-200 grid grid-cols-2 md:grid-cols-4">
          {[
            { k: "Projects Delivered", v: stats.projects_delivered, suffix: "+" },
            { k: "Years of Practice", v: stats.years_experience, suffix: "+" },
            { k: "Clients Served", v: stats.clients_served, suffix: "+" },
            { k: "Team Members", v: stats.team_size, suffix: "" },
          ].map((s, i) => (
            <div
              key={s.k}
              className={`p-4 md:p-6 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} ${i === 1 ? "md:border-r" : ""} ${i === 2 ? "md:border-r" : ""} border-zinc-200`}
            >
              <div className="font-display text-3xl md:text-4xl font-light text-black leading-none">
                <Counter value={s.v} suffix={s.suffix} />
              </div>
              <div className="mono-label text-zinc-500 mt-2 text-[9px] md:text-[10px]">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section className="py-14 md:py-20 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="mono-label text-zinc-500">About · 01</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight text-balance">
                An engineering practice that owns the outcome.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-3.5 text-zinc-700 text-sm md:text-base leading-relaxed">
            <Reveal delay={0.1}>
              <p>
                Mohr Engineering is a full-service contractor built around one idea: that design and delivery should answer to the same team. From geotechnical investigation to fit-out, we keep accountability under one roof.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p>
                Our work is quiet, structural, and measurable. We let our finished buildings, roads and water systems speak for us — across institutional, residential and infrastructure sectors.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" className="btn-outline mt-2" data-testid="about-cta">
                Read Our Story <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#0A0A0A] text-white py-14 md:py-20" data-testid="home-services">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10 md:mb-14">
            <Reveal>
              <div className="mono-label text-zinc-500">What We Do · 02</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight max-w-2xl">
                Four disciplines. One integrated delivery team.
              </h2>
            </Reveal>
            <Link to="/services" className="btn-outline-light self-start">
              All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
            {SERVICES.map((s, i) => (
              <Reveal key={s.code} delay={i * 0.08}>
                <div className="bg-[#0A0A0A] p-5 md:p-7 group h-full hover:bg-[#171717] transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="mono-label text-[#BE1E2D]">{s.code}</div>
                      <h3 className="font-display text-xl md:text-2xl mt-2.5 mb-2.5 leading-tight">{s.title}</h3>
                      <p className="text-zinc-400 text-sm md:text-[15px] leading-relaxed max-w-md">{s.body}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-zinc-600 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-14 md:py-20 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14" data-testid="home-featured-projects">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10 md:mb-14">
          <Reveal>
            <div className="mono-label text-zinc-500">Featured Work · 03</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight max-w-2xl">
              Selected projects from across Nigeria.
            </h2>
          </Reveal>
          <Link to="/portfolio" className="btn-outline self-start">
            Full Portfolio <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-7">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <Link to={`/portfolio/${p.slug}`} className="block group" data-testid={`featured-project-${p.slug}`}>
                <div className="img-hover-scale aspect-[4/3] bg-zinc-100">
                  {p.cover_image && (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="flex items-start justify-between gap-3 mt-3 md:mt-4">
                  <div>
                    <div className="mono-label text-zinc-500">{p.category} · {p.year}</div>
                    <h3 className="font-display text-lg md:text-xl lg:text-2xl mt-1.5 leading-tight group-hover:text-[#BE1E2D] transition-colors">{p.title}</h3>
                    <div className="text-zinc-600 mt-1 text-xs md:text-sm">{p.client}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-zinc-400 group-hover:text-black group-hover:-translate-y-1 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F9F9F8] py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="mono-label text-zinc-500">Principles · 04</div>
              <h2 className="font-display text-3xl md:text-4xl mt-4 leading-[1.05] tracking-tight">
                What it means to build with us.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-zinc-200">
            {VALUES.map((v, i) => (
              <Reveal key={v.k} delay={i * 0.06}>
                <div className="bg-[#F9F9F8] p-6 md:p-8 h-full">
                  <div className="font-display text-xl md:text-2xl">{v.k}</div>
                  <p className="text-zinc-600 mt-3 leading-relaxed text-[15px]">{v.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="py-12 border-y border-zinc-200 overflow-hidden">
        <div className="mono-label text-zinc-500 text-center mb-4">Trusted by</div>
        <div className="overflow-hidden">
          <div className="marquee-track gap-12 items-center">
            {[...CLIENTS, ...CLIENTS].map((c, i) => (
              <span key={i} className="font-display text-xl md:text-2xl text-zinc-400 whitespace-nowrap px-3">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0A0A0A] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div className="mono-label text-zinc-500 mb-6">Let's Build · 05</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight mb-8">
              Your project.<br />Our signature.
            </h2>
            <p className="text-zinc-400 text-base md:text-lg max-w-lg mx-auto mb-8">
              Share your brief. We will respond within one working day.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="home-bottom-cta">
              Start a Project <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
